import { SUBJECTS } from "./utils"

export interface ParsedFilename {
  year?: number
  region?: string
  subject?: string
  title?: string
  confidence?: number // 0-100: confidence score for auto-parsing
}

/**
 * Parse exam paper metadata from filename
 * Examples:
 * - 2020年新高考全国II卷.pdf
 * - 2022年全国甲卷.pdf
 * - 【试卷】2025年全国一卷数学原版.pdf
 * - 2023年新课标全国I卷.pdf
 */
export function parseFilename(filename: string): ParsedFilename {
  // Remove file extension and common brackets
  const cleanName = filename
    .replace(/\.pdf$/i, "")
    .replace(/【.*?】/g, "")
    .replace(/\(.*?\)/g, "")
    .trim()

  const result: ParsedFilename = {}
  let confidence = 0
  let maxConfidence = 3 // year, region, subject

  // Extract year (e.g., "2020年", "2021年")
  const yearMatch = cleanName.match(/(\d{4})年/)
  if (yearMatch) {
    const year = parseInt(yearMatch[1])
    if (year >= 1950 && year <= new Date().getFullYear() + 1) {
      result.year = year
      confidence++
    }
  }

  // Extract subject from the list of known subjects
  const subjectMatch = SUBJECTS.find(subject => cleanName.includes(subject))
  if (subjectMatch) {
    result.subject = subjectMatch
    confidence++
  } else {
    // Default to 数学 (Math) if no subject is detected
    result.subject = "数学"
    // Don't increment confidence for default
  }

  // Extract region patterns
  // Common patterns: 全国X卷, 新高考全国X卷, 新课标全国X卷, etc.
  let regionMatch = cleanName.match(
    /(新高考|新课标)?(全国|北京|上海|天津|重庆|浙江|江苏|广东|山东|湖北|湖南|河北|河南|辽宁|四川|福建|安徽|江西|海南)([一二三]|I{1,3}|[甲乙丙])?卷?/
  )

  if (regionMatch) {
    let region = regionMatch[0]

    // Normalize region name
    region = normalizeRegion(region)
    result.region = region
    confidence++
  }

  // Generate title from parsed components or use cleaned filename
  if (result.year) {
    const titleParts = []
    titleParts.push(`${result.year}年`)

    if (result.region) {
      titleParts.push(result.region)
    }

    // Don't include subject in the auto-generated title
    // The subject is already set separately in the form

    // If we have at least year and region, use the constructed title
    if (result.region) {
      result.title = titleParts.join("")
    }
  }

  // Fallback: use cleaned filename if no title was constructed
  if (!result.title && cleanName) {
    result.title = cleanName
  }

  // Calculate confidence percentage
  result.confidence = Math.round((confidence / maxConfidence) * 100)

  return result
}

/**
 * Normalize region names to consistent format
 * Examples:
 * - 全国一卷 → 全国I卷
 * - 全国二卷 → 全国II卷
 * - 全国甲卷 → 全国甲卷 (unchanged)
 */
function normalizeRegion(region: string): string {
  // Convert Chinese numbers to Roman numerals for consistency
  const replacements: Record<string, string> = {
    "一": "I",
    "二": "II",
    "三": "III"
  }

  let normalized = region

  // Replace Chinese numerals with Roman numerals
  for (const [chinese, roman] of Object.entries(replacements)) {
    if (normalized.includes(chinese)) {
      normalized = normalized.replace(chinese, roman)
      break
    }
  }

  // Ensure it ends with "卷" if not already
  if (!normalized.endsWith("卷")) {
    normalized += "卷"
  }

  return normalized
}
